import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { useKitchen } from "../../context/KitchenContext";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import { FaEdit, FaTrash } from "react-icons/fa";
import AddIcon from "@mui/icons-material/Add";

const KitchenListings = () => {
  const { user, loading: authLoading } = useContext(AuthContext);
  const { fetchAllKitchens, allKitchens, setAllKitchens } = useKitchen();
  const [kitchensLoaded, setKitchensLoaded] = useState(false);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const fetchKitchens = async () => {
      if (user?.userID) {
        try {
          const kitchens = await fetchAllKitchens({
            userID: user.userID,
            forceRefresh: true,
          });
          setAllKitchens(kitchens.reverse());
          setKitchensLoaded(true);
        } catch (error) {
          console.error("Error fetching kitchens:", error);
          setKitchensLoaded(true);
        }
      }
    };
    fetchKitchens();
  }, [fetchAllKitchens, setAllKitchens, user?.userID]);

  const filteredKitchens =
    filter === "all"
      ? allKitchens
      : allKitchens.filter(
          (kitchen) => kitchen.KitchenStatus.toLowerCase() === filter
        );

  if (authLoading) {
    return <Box className="loading">Loading user data...</Box>;
  }

  if (!user) {
    return (
      <Box className="loading">
        Please log in or sign up to view your kitchens.
      </Box>
    );
  }

  if (!kitchensLoaded) {
    return (
      <Box className="loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      className="kitchen-listings-section"
      sx={{ maxWidth: "1200px", mx: "auto", bgcolor: "#ffffff" }}
    >
      <Box>
        <Box
          className="kitchen-listings-header"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5" color="text.primary">
            My Kitchens
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Filter</InputLabel>
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                label="Filter"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="approve">Approved</MenuItem>
                <MenuItem value="archive">Archived</MenuItem>
              </Select>
            </FormControl>
            <Button
              component={Link}
              to="/profile/add-kitchen"
              className="add-kitchen-btn"
              variant="contained"
              color="primary"
              sx={{
                minWidth: "auto",
                width: 40,
                height: 40,
                borderRadius: "50%",
                padding: 0,
              }}
            >
              <AddIcon />
            </Button>
          </Box>
        </Box>
        {filteredKitchens.length === 0 ? (
          <Box
            className="no-kitchens"
            sx={{ textAlign: "center", py: 4, bgcolor: "#ffffff" }}
          >
            <Typography variant="body1" color="text.secondary">
              No kitchens available.{" "}
              <Link to="/profile/add-kitchen">Add a kitchen</Link> to get
              started.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredKitchens.map((kitchen) => (
              <Grid item xs={12} sm={6} md={4} key={kitchen.KitchenID}>
                <Box className="kitchen-item">
                  <Box className="my-kitchen-image">
                    <img
                      src={
                        kitchen.Images[0] ||
                        "https://airkitchenz-assets.s3.us-west-1.amazonaws.com/assets/KitchenComingSoon.png"
                      }
                      alt={kitchen.Title}
                    />
                    <Box className="image-overlay">
                      <Typography variant="h6" className="overlay-title">
                        {kitchen.Title || "Kitchen"}
                      </Typography>
                      <Box className="overlay-details">
                        <Typography variant="body2" className="overlay-city">
                          {kitchen.City}
                        </Typography>
                        <Typography variant="body2" className="overlay-rating">
                          ⭐ {kitchen.Rating || "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                    <span
                      className={`badge badge-${kitchen.KitchenStatus.toLowerCase()}`}
                    >
                      {kitchen.KitchenStatus}
                    </span>
                    <Box className="icon-actions">
                      <IconButton
                        component={Link}
                        to={`/profile/edit-kitchen/${kitchen.KitchenID}`}
                        color="primary"
                        sx={{ padding: 1, color: "red" }}
                      >
                        <FaEdit size={18} />
                      </IconButton>
                      <IconButton
                        color="secondary"
                        sx={{ padding: 1, color: "red" }}
                      >
                        <FaTrash size={18} />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default KitchenListings;
